import { createClient, FunctionInvokeOptions } from '@supabase/supabase-js';
import type { FunctionsResponse } from '@supabase/functions-js';
import { FunctionsError, FunctionsHttpError } from '@supabase/functions-js';

import { Database } from './databaseTypes';

export const SUPABASE_URL = 'https://yjfqkhspuatsgrwmgkww.supabase.co';
export const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlqZnFraHNwdWF0c2dyd21na3d3Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc1NDg0MDEsImV4cCI6MjA1MzEyNDQwMX0.fSjFsWIE9HIbLEOWvJ2oJK7s_8Ku4ivH1HZ_Msn6upY';

export const client = createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY);

const DEFAULT_INVOKE_TIMEOUT_MS = 20_000;

export class InvokeTimeoutError extends Error {
    readonly functionName: string;
    readonly timeoutMs: number;

    constructor(functionName: string, timeoutMs: number) {
        super(`Edge function "${functionName}" timed out after ${timeoutMs}ms`);
        this.name = 'InvokeTimeoutError';
        this.functionName = functionName;
        this.timeoutMs = timeoutMs;
    }
}

/**
 * Stand-in for `FunctionsHttpError` that actually says what went wrong.
 *
 * The supabase SDK throws a bare `FunctionsHttpError` with the hard-coded
 * message `"Edge Function returned a non-2xx status code"` and stuffs the raw
 * `Response` onto `.context`. That message is what ends up in Sentry — useless
 * for triage. We unwrap it once at the boundary so callers (and Sentry) see
 * function name, status, and parsed body.
 */
export class EdgeFunctionError extends FunctionsError {
    readonly functionName: string;
    readonly status: number;
    readonly responseBody: unknown;

    constructor(functionName: string, status: number, responseBody: unknown) {
        const bodyText = typeof responseBody === 'string'
            ? responseBody
            : responseBody != null ? JSON.stringify(responseBody) : '';
        const suffix = bodyText ? `: ${bodyText.slice(0, 500)}` : '';
        super(
            `Edge function "${functionName}" failed (${status})${suffix}`,
            'EdgeFunctionError',
            { functionName, status, responseBody },
        );
        this.functionName = functionName;
        this.status = status;
        this.responseBody = responseBody;
    }
}

async function readResponseBody(response: Response): Promise<unknown> {
    try {
        const text = await response.clone().text();
        if (!text) {
            return undefined;
        }
        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    } catch {
        return undefined;
    }
}

async function enrichFunctionsHttpError<T>(
    functionName: string,
    result: FunctionsResponse<T>,
): Promise<FunctionsResponse<T>> {
    if (!(result.error instanceof FunctionsHttpError)) {
        return result;
    }

    const response = result.error.context as Response | undefined;
    if (response == null) {
        return result;
    }

    const body = await readResponseBody(response);
    return {
        data: null,
        error: new EdgeFunctionError(functionName, response.status, body),
    };
}

/**
 * Wraps `client.functions.invoke` with a soft timeout so a stuck edge call can't
 * pin a UI flow indefinitely.
 *
 * supabase-js 2.48 / functions-js 2.4 does not forward `signal` to the underlying
 * fetch, so we race the invoke against a timer instead of aborting. On timeout
 * the caller sees a normal `{ data: null, error: InvokeTimeoutError }` shape and
 * the orphaned fetch completes in the background — acceptable for short-lived
 * edge calls.
 *
 * Non-2xx responses come back as `EdgeFunctionError` instead of the SDK's bare
 * `FunctionsHttpError`, so Sentry and callers see a real message + status.
 */
export async function invokeWithTimeout<T = unknown>(
    functionName: string,
    options?: FunctionInvokeOptions,
    timeoutMs: number = DEFAULT_INVOKE_TIMEOUT_MS,
): Promise<FunctionsResponse<T>> {
    let timer: ReturnType<typeof setTimeout> | undefined;

    const timeout = new Promise<FunctionsResponse<T>>((resolve) => {
        timer = setTimeout(() => {
            resolve({ data: null, error: new InvokeTimeoutError(functionName, timeoutMs) });
        }, timeoutMs);
    });

    try {
        const result = await Promise.race([
            client.functions.invoke<T>(functionName, options),
            timeout,
        ]);
        return await enrichFunctionsHttpError(functionName, result);
    } finally {
        if (timer != null) {
            clearTimeout(timer);
        }
    }
}
